import { useMemo } from 'react';
import type { PortfolioAmount, PortfolioEnterpriseProductServiceProductData } from '@mews-commander/data-access';
import { Button, IconName, Stack, Typography } from '@optimus-web/core';
import { lt, useFeatureFlag } from '@mews/framework';
import { OptimusProvider } from '@mews-commander/ui';
import { useDialogForm } from '@mews-commander/form';
import { FeatureFlag } from '@mews-commander/enums';
import type { LocalProductSidePanelPropsWithData } from './types';
import { ServiceCard } from './ServiceCard';
import { AddProductToServiceForm } from './forms/AddProductToServiceForm';

export type LocalProductServicesProps = LocalProductSidePanelPropsWithData<{
    servicesData: PortfolioEnterpriseProductServiceProductData[];
    basePriceValue?: PortfolioAmount;
}>;

export const LocalProductServices = ({
    servicesData,
    basePriceValue,
    enterpriseProductId,
    propertyId,
    centralProductId,
    ...propagatedButtonProps
}: LocalProductServicesProps) => {
    const addToServiceButtonEnabled = useFeatureFlag(FeatureFlag.addToServiceButtonEnabled);
    const centralProductsEditEnabled = useFeatureFlag(FeatureFlag.centralProductsEditEnabled);
    const serviceIdsInUse = useMemo(() => servicesData.map(({ service: { id } }) => id), [servicesData]);
    const showAddToServiceButton = centralProductsEditEnabled || addToServiceButtonEnabled;
    const [renderAddProductToServiceForm, toggleAddProductToServiceForm] = useDialogForm({
        title: lt('AddProductToService'),
        cancelLabel: lt('GoBack'),
        renderForm: (formProps) => (
            <AddProductToServiceForm
                {...formProps}
                serviceIdsInUse={serviceIdsInUse}
                formRef={formProps.formRef}
                centralProductId={centralProductId as string}
                enterpriseProductId={enterpriseProductId}
                propertyId={propertyId}
                basePriceValue={basePriceValue}
            />
        ),
    });

    if (!servicesData.length && !showAddToServiceButton) {
        return (
            <Typography textStyle="bodyMedium" variant="tertiary" as="div">
                {lt('ProductNoServicesConfigured')}
            </Typography>
        );
    }

    return (
        <OptimusProvider density="spacious">
            <Stack spacing="400" vertical>
                <Stack itemAlignment="center" distribution="space-between" spacing="200">
                    <Typography textStyle="titleSmallStrong" as="div">
                        {lt('GeneralServices')}
                    </Typography>
                    {centralProductsEditEnabled && addToServiceButtonEnabled ? (
                        <Button
                            variant="tertiary"
                            type="button"
                            icon={IconName.Add}
                            size="small"
                            onClick={toggleAddProductToServiceForm}
                        >
                            {lt('AddToService')}
                        </Button>
                    ) : null}
                </Stack>
                <Stack spacing="200" vertical>
                    {!servicesData.length && showAddToServiceButton ? (
                        <Typography textStyle="bodyMedium" variant="tertiary" as="div">
                            {lt('CentralProductNoServicesConfigured')}
                        </Typography>
                    ) : (
                        servicesData.map((service) => (
                            <ServiceCard
                                {...service}
                                {...propagatedButtonProps}
                                key={service.service.id}
                                basePriceValue={basePriceValue}
                                centralProductId={centralProductId}
                                enterpriseProductId={enterpriseProductId}
                                propertyId={propertyId}
                            />
                        ))
                    )}
                </Stack>
            </Stack>
            {renderAddProductToServiceForm()}
        </OptimusProvider>
    );
};
